import { useReducer } from 'react';
import {
  ConnectedConnection,
  OutgoingConnection,
  UserConnection,
} from '../model/UserConnection';
import {
  FetchUserConnectionResult,
  useFetchUserConnection,
} from './useFetchUserConnection';
import { uniqBy } from 'lodash';

export type ConnectionAction =
  | BeforeFetchConnection
  | BeforeFetchMoreIncomingConnection
  | BeforeFetchMoreOutgoingConnection
  | BeforeFetchMoreConnectedConnection
  | FetchConnection
  | FetchMoreIncomingConnections
  | FetchMoreOutgoingConnections
  | FetchMoreConnectedConnections
  | RejectConnectionRequest
  | AcceptConnectionRequest
  | CancelConnectionRequest
  | RequestConnect
  | DisconnectConnection;

type BeforeFetchConnection = {
  type: 'BEFORE_FETCH_CONNECTION';
};

type BeforeFetchMoreIncomingConnection = {
  type: 'BEFORE_FETCH_MORE_INCOMING_CONNECTION';
};

type BeforeFetchMoreOutgoingConnection = {
  type: 'BEFORE_FETCH_MORE_OUTGOING_CONNECTION';
};

type BeforeFetchMoreConnectedConnection = {
  type: 'BEFORE_FETCH_MORE_CONNECTED_CONNECTION';
};

type FetchConnection = {
  type: 'FETCH_CONNECTION';
  payload: FetchUserConnectionResult;
};

type FetchMoreIncomingConnections = {
  type: 'FETCH_MORE_INCOMING_CONNECTIONS';
  payload: IncomingConnections;
};

type FetchMoreOutgoingConnections = {
  type: 'FETCH_MORE_OUTGOING_CONNECTIONS';
  payload: OutgoingConnections;
};

type FetchMoreConnectedConnections = {
  type: 'FETCH_MORE_CONNECTED_CONNECTIONS';
  payload: ConnectedConnections;
};

type RejectConnectionRequest = {
  type: 'REJECT_INCOMING_CONNECTION_REQUEST';
  payload: UserConnection;
};

type AcceptConnectionRequest = {
  type: 'ACCEPT_INCOMING_CONNECTION_REQUEST';
  payload: UserConnection;
};

type CancelConnectionRequest = {
  type: 'CANCEL_CONNECTION_REQUEST';
  payload: UserConnection;
};

type RequestConnect = {
  type: 'REQUEST_CONNECT';
  payload: UserConnection;
};

type DisconnectConnection = {
  type: 'DISCONNECT_CONNECTION';
  payload: UserConnection;
};

export type IncomingConnections = {
  connections: UserConnection[];
  loading: boolean;
  hasMore: boolean;
  page: number;
};

export type ConnectedConnections = {
  connections: ConnectedConnection[];
  loading: boolean;
  hasMore: boolean;
  page: number;
};

export type OutgoingConnections = {
  connections: OutgoingConnection[];
  loading: boolean;
  hasMore: boolean;
  page: number;
};

export type ConnectionStore = {
  incomingConnections: IncomingConnections;
  outgoingConnections: OutgoingConnections;
  connectedConnections: ConnectedConnections;
};

const DEFAULT_CONNECTION_STORE: ConnectionStore = {
  incomingConnections: {
    connections: [],
    loading: false,
    hasMore: false,
    page: 0,
  },
  outgoingConnections: {
    connections: [],
    loading: false,
    hasMore: false,
    page: 0,
  },
  connectedConnections: {
    connections: [],
    loading: false,
    hasMore: false,
    page: 0,
  },
};

export const useUserConnection = () => {
  const [connectionStore, dispatch] = useReducer(
    reducer,
    DEFAULT_CONNECTION_STORE,
  );

  useFetchUserConnection(dispatch);
  return {
    connectionDispatch: dispatch,
    incomingConnections: connectionStore.incomingConnections,
    outgoingConnections: connectionStore.outgoingConnections,
    connectedConnections: connectionStore.connectedConnections,
  };
};

const reducer = (
  store: ConnectionStore,
  action: ConnectionAction,
): ConnectionStore => {
  switch (action.type) {
    case 'BEFORE_FETCH_CONNECTION':
      return beforeFetchUserConnection(store);
    case 'BEFORE_FETCH_MORE_INCOMING_CONNECTION':
      return beforeFetchMoreIncomingConnection(store);
    case 'BEFORE_FETCH_MORE_OUTGOING_CONNECTION':
      return beforeFetchMoreOutgoingConnection(store);
    case 'BEFORE_FETCH_MORE_CONNECTED_CONNECTION':
      return beforeFetchMoreConnectedConnection(store);
    case 'FETCH_CONNECTION':
      return setUserConnection(store, action.payload);
    case 'FETCH_MORE_INCOMING_CONNECTIONS':
      return mergeIncomingConnections(store, action.payload);
    case 'FETCH_MORE_OUTGOING_CONNECTIONS':
      return mergeOutgoingConnections(store, action.payload);
    case 'FETCH_MORE_CONNECTED_CONNECTIONS':
      return mergeConnectedConnections(store, action.payload);
    case 'REJECT_INCOMING_CONNECTION_REQUEST':
      return rejectConnectionRequest(store, action.payload);
    case 'ACCEPT_INCOMING_CONNECTION_REQUEST':
      return acceptConnectionRequest(store, action.payload);
    case 'CANCEL_CONNECTION_REQUEST':
      return cancelConnectionRequest(store, action.payload);
    case 'REQUEST_CONNECT':
      return requestConnect(store, action.payload);
    case 'DISCONNECT_CONNECTION':
      return disconnectConnection(store, action.payload);
    default:
      throw Error(`Unsupported action type ${action}`);
  }
};

const beforeFetchUserConnection = (store: Readonly<ConnectionStore>) => {
  const fetchingIncomingConnections = {
    ...store.incomingConnections,
    loading: true,
  };
  const fetchingOutgoingConnections = {
    ...store.outgoingConnections,
    loading: true,
  };
  const fetchingConnectedConnections = {
    ...store.connectedConnections,
    loading: true,
  };
  return {
    incomingConnections: fetchingIncomingConnections,
    outgoingConnections: fetchingOutgoingConnections,
    connectedConnections: fetchingConnectedConnections,
  };
};

const beforeFetchMoreIncomingConnection = (
  connectionStore: Readonly<ConnectionStore>,
) => {
  const newStore = { ...connectionStore };
  newStore.incomingConnections = {
    ...newStore.incomingConnections,
    loading: true,
  };

  return newStore;
};

const beforeFetchMoreOutgoingConnection = (
  connectionStore: Readonly<ConnectionStore>,
) => {
  const newStore = { ...connectionStore };
  newStore.outgoingConnections = {
    ...newStore.outgoingConnections,
    loading: true,
  };

  return newStore;
};

const beforeFetchMoreConnectedConnection = (
  connectionStore: Readonly<ConnectionStore>,
) => {
  const newStore = { ...connectionStore };
  newStore.connectedConnections = {
    ...newStore.connectedConnections,
    loading: true,
  };

  return newStore;
};

const setUserConnection = (
  store: Readonly<ConnectionStore>,
  fetchedConnection: FetchUserConnectionResult,
): ConnectionStore => {
  const connectedConnections =
    fetchedConnection.connectedConnections.connections.map((connection) => ({
      ...connection,
      highlighted: false,
    }));
  return {
    incomingConnections: {
      ...fetchedConnection.incomingConnections,
      loading: false,
      page: 0,
    },
    outgoingConnections: {
      ...fetchedConnection.outgoingConnections,
      loading: false,
      page: 0,
    },
    connectedConnections: {
      connections: connectedConnections,
      hasMore: fetchedConnection.connectedConnections.hasMore,
      loading: false,
      page: 0,
    },
  };
};

const mergeIncomingConnections = (
  connectionStore: Readonly<ConnectionStore>,
  addedConnections: IncomingConnections,
) => {
  const newStore = { ...connectionStore };
  const incomingConnections = newStore.incomingConnections;
  const mergedConnections = uniqBy(
    [...incomingConnections.connections, ...addedConnections.connections],
    (connection) => connection.guid,
  );
  newStore.incomingConnections = {
    ...addedConnections,
    connections: mergedConnections,
  };

  return newStore;
};

const mergeOutgoingConnections = (
  connectionStore: Readonly<ConnectionStore>,
  addedConnections: OutgoingConnections,
) => {
  const newStore = { ...connectionStore };
  const outgoingConnections = newStore.outgoingConnections;
  const mergedConnections = uniqBy(
    [...outgoingConnections.connections, ...addedConnections.connections],
    (connection) => connection.guid,
  );
  newStore.outgoingConnections = {
    ...addedConnections,
    connections: mergedConnections,
  };

  return newStore;
};

const mergeConnectedConnections = (
  connectionStore: Readonly<ConnectionStore>,
  addedConnections: IncomingConnections,
) => {
  const newStore = { ...connectionStore };
  const connectedConnections = newStore.connectedConnections;
  const mergedConnections = uniqBy(
    [...connectedConnections.connections, ...addedConnections.connections],
    (connection) => connection.guid,
  );
  newStore.connectedConnections = {
    ...addedConnections,
    connections: mergedConnections,
  };

  return newStore;
};

const rejectConnectionRequest = (
  store: Readonly<ConnectionStore>,
  rejectedConnection: UserConnection,
): ConnectionStore => {
  const newStore = { ...store };
  newStore.incomingConnections.connections =
    newStore.incomingConnections.connections.filter(
      (connection) => connection.guid !== rejectedConnection.guid,
    );

  return newStore;
};

const acceptConnectionRequest = (
  store: Readonly<ConnectionStore>,
  connection: UserConnection,
): ConnectionStore => {
  const acceptedConnection: ConnectedConnection = {
    ...connection,
    highlighted: true,
  };
  const newStore = { ...store };
  newStore.incomingConnections.connections =
    newStore.incomingConnections.connections.filter(
      (c) => c.guid !== connection.guid,
    );
  newStore.connectedConnections = {
    ...newStore.connectedConnections,
    connections: uniqBy(
      [acceptedConnection, ...newStore.connectedConnections.connections],
      (connection) => connection.guid,
    ),
  };

  return newStore;
};

const cancelConnectionRequest = (
  store: Readonly<ConnectionStore>,
  canceledConnection: UserConnection,
): ConnectionStore => {
  const newStore = { ...store };
  newStore.outgoingConnections.connections.forEach((connection) => {
    if (connection.guid === canceledConnection.guid) {
      connection.canceledRequest = true;
    }
  });

  return newStore;
};

const requestConnect = (
  store: Readonly<ConnectionStore>,
  connection: UserConnection,
) => {
  const newStore = { ...store };
  const requestedConnection: OutgoingConnection = {
    ...connection,
    canceledRequest: false,
  };
  const connections = newStore.outgoingConnections.connections.map((c) =>
    c.guid === connection.guid ? requestedConnection : c,
  );
  const updatedConnectedConnections =
    newStore.connectedConnections.connections.filter(
      (c) => c.guid !== connection.guid,
    );
  newStore.outgoingConnections.connections = connections;
  newStore.connectedConnections.connections = updatedConnectedConnections;

  return newStore;
};

const disconnectConnection = (
  store: Readonly<ConnectionStore>,
  disconnectedConnection: UserConnection,
): ConnectionStore => {
  const connectedConnections = store.connectedConnections.connections.map(
    (connection) => {
      if (connection.guid === disconnectedConnection.guid) {
        return { ...connection, isDisConnect: true };
      }
      return connection;
    },
  );
  const newStore = { ...store };
  newStore.connectedConnections.connections = connectedConnections;

  return newStore;
};
